/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { Input } from "baseui/input";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { DexterModal } from "../Shared/DexterModal";

export interface NamePromptModalProps {
  isOpen: boolean;
  title: string;
  confirmLabel: string;
  initialName?: string;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}

/**
 * Prompts for a name on the shared DexterModal — just the name field plus the
 * base modal's Cancel / Confirm. Used both for creating a new folder and for
 * renaming an existing folder or file. Confirm is disabled until a non-empty
 * name is entered.
 */
export const NamePromptModal = ({
  isOpen,
  title,
  confirmLabel,
  initialName = "",
  onConfirm,
  onCancel,
}: NamePromptModalProps): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const [name, setName] = React.useState(initialName);

  // Reset the field to the starting value whenever the modal is (re)opened.
  React.useEffect(() => {
    if (isOpen) {
      setName(initialName);
    }
  }, [isOpen, initialName]);

  const trimmedName = name.trim();
  const canConfirm = trimmedName.length > 0;

  const handleConfirm = (): void => {
    if (canConfirm) {
      onConfirm(trimmedName);
    }
  };

  return (
    <DexterModal
      isOpen={isOpen}
      title={title}
      confirmLabel={confirmLabel}
      confirmDisabled={!canConfirm}
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      <Input
        value={name}
        onChange={(e) => setName((e.target as HTMLInputElement).value)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            handleConfirm();
          }
        }}
        placeholder="Name"
        autoFocus
        overrides={{
          Input: {
            style: {
              backgroundColor: currentTheme.colors.dropdownBackground,
              color: currentTheme.colors.dropdownText,
            },
          },
        }}
      />
    </DexterModal>
  );
};
